import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface BalanceAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  currentBalance: number; // in cents, negative = credit
  onAdjustmentComplete: () => void;
}

interface BalanceTransaction {
  id: string;
  amount: number;
  type: 'credit' | 'debit';
  description: string;
  created: number;
  ending_balance: number;
}

const BalanceAdjustmentModal: React.FC<BalanceAdjustmentModalProps> = ({
  isOpen,
  onClose,
  studentId,
  studentName,
  currentBalance,
  onAdjustmentComplete,
}) => {
  const { getAuthToken } = useAuth();
  const [adjustmentType, setAdjustmentType] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const quickAmounts = [5, 10, 25, 50];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const amountInCents = Math.round(parseFloat(amount) * 100);

    if (isNaN(amountInCents) || amountInCents <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/customers/${studentId}/balance-adjustment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            amount: amountInCents,
            type: adjustmentType,
            description: description.trim(),
            reason: 'manual_adjustment',
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create adjustment');
      }

      // Reset form
      setAmount('');
      setDescription('');
      
      // Notify parent and close
      onAdjustmentComplete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create adjustment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadTransactionHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/customers/${studentId}/balance-transactions?limit=10`,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error('Failed to load transaction history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleShowHistory = () => {
    setShowHistory(!showHistory);
    if (!showHistory && transactions.length === 0) {
      loadTransactionHistory();
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  const balanceDisplay = currentBalance < 0 
    ? `${formatCurrency(Math.abs(currentBalance))} credit`
    : currentBalance > 0
    ? `${formatCurrency(currentBalance)} owed`
    : '$0.00';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Adjust Balance</h2>
              <p className="text-sm text-gray-600 mt-1">{studentName}</p>
              <p className="text-lg font-semibold mt-2">
                Current Balance: <span className={currentBalance < 0 ? 'text-green-600' : currentBalance > 0 ? 'text-red-600' : 'text-gray-600'}>{balanceDisplay}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Important Notice */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Balance adjustments are applied to the <strong>next billing cycle</strong>. 
              For immediate charges, use the <strong>One-Time Payment</strong> feature instead.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Adjustment Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adjustment Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="credit"
                  checked={adjustmentType === 'credit'}
                  onChange={(e) => setAdjustmentType(e.target.value as 'credit' | 'debit')}
                  className="mr-2"
                />
                <span className="text-green-600 font-medium">Credit (Give Money)</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="debit"
                  checked={adjustmentType === 'debit'}
                  onChange={(e) => setAdjustmentType(e.target.value as 'credit' | 'debit')}
                  className="mr-2"
                />
                <span className="text-red-600 font-medium">Debit (Charge Money)</span>
              </label>
            </div>
          </div>

          {/* Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div className="flex gap-2 mt-2">
              {quickAmounts.map((quickAmount) => (
                <button
                  key={quickAmount}
                  type="button"
                  onClick={() => setAmount(quickAmount.toString())}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  ${quickAmount}
                </button>
              ))}
            </div>
          </div>

          {/* Reason Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a description for this adjustment..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Preview */}
          {amount && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
              <p className="text-lg">
                {adjustmentType === 'credit' ? (
                  <span className="text-green-600 font-semibold">
                    +{formatCurrency(parseFloat(amount || '0') * 100)} credit
                  </span>
                ) : (
                  <span className="text-red-600 font-semibold">
                    +{formatCurrency(parseFloat(amount || '0') * 100)} charge
                  </span>
                )}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                New balance: {formatCurrency(
                  currentBalance + (adjustmentType === 'credit' ? -1 : 1) * (parseFloat(amount || '0') * 100)
                )}
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-between items-center gap-4">
            <button
              type="button"
              onClick={handleShowHistory}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              {showHistory ? 'Hide History' : 'View History'}
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Processing...' : 'Apply Adjustment'}
              </button>
            </div>
          </div>
        </form>

        {/* Transaction History */}
        {showHistory && (
          <div className="px-6 py-4 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Transaction History</h3>
            {isLoadingHistory ? (
              <p className="text-gray-500 text-center py-4">Loading...</p>
            ) : transactions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No transactions yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {transactions.map((txn) => (
                  <div
                    key={txn.id}
                    className="flex justify-between items-start p-3 bg-gray-50 rounded-md"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{txn.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(txn.created)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {txn.type === 'credit' ? '+' : '-'}{formatCurrency(Math.abs(txn.amount))}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Balance: {formatCurrency(txn.ending_balance)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceAdjustmentModal;

